import { useEffect, useRef } from 'react';
import { commandForKey, isMacPlatform, type MarkupCommand } from './markupShortcuts';

interface UseMarkupShortcutsArgs {
    enabled: boolean;
    hasSelection: boolean;
    onCommand: (command: MarkupCommand) => void;
}

const EDITABLE_TAGS: ReadonlySet<string> = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const IS_MAC = isMacPlatform();

function isEditable(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return EDITABLE_TAGS.has(target.tagName) || target.isContentEditable;
}

// Capture phase, so Escape can clear a selection before the dialog's own Escape closes it.
export function useMarkupShortcuts({
    enabled,
    hasSelection,
    onCommand
}: UseMarkupShortcutsArgs): void {
    const latest = useRef({ hasSelection, onCommand });

    useEffect(() => {
        latest.current = { hasSelection, onCommand };
    }, [hasSelection, onCommand]);

    useEffect(() => {
        if (!enabled) return undefined;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.isComposing || isEditable(event.target)) return;
            const command = commandForKey(event, IS_MAC);
            if (!command) return;
            const needsSelection = command.type === 'delete' || command.type === 'deselect';
            if (needsSelection && !latest.current.hasSelection) return;
            event.preventDefault();
            if (command.type === 'deselect') event.stopPropagation();
            latest.current.onCommand(command);
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [enabled]);
}
